import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Humanoid } from './Humanoid';

export enum LanderState {
  DESCENDING = 'descending',
  SEEKING_HUMANOID = 'seeking_humanoid',
  ABDUCTING = 'abducting',
  ASCENDING = 'ascending',
  MUTATING = 'mutating'
}

export class Lander implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;
  public type: string = 'lander';

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private state: LanderState = LanderState.DESCENDING;
  private targetHumanoid: Humanoid | null = null;
  private abductedHumanoid: Humanoid | null = null;
  private speed: number = 80;
  private animationTime: number = 0;
  private animationFrame: number = 0;
  private worldWidth: number;
  private descendTarget: number; // Y position to descend to
  private cameraX: number = 0; // Store camera position for rendering
  private cachedHumanoids: Humanoid[] = []; // Cache humanoids reference

  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    worldWidth: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: 0, y: this.speed };
    this.size = { x: 12, y: 8 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.worldWidth = worldWidth;
    this.descendTarget = 400 + Math.random() * 100; // Random descent depth
  }

  public update(deltaTime: number): void {
    const humanoids = this.cachedHumanoids; // Use cached humanoids
    // Update animation
    this.animationTime += deltaTime;
    if (this.animationTime >= 0.3) {
      this.animationTime = 0;
      this.animationFrame = (this.animationFrame + 1) % 2;
    }

    switch (this.state) {
      case LanderState.DESCENDING:
        this.updateDescending(deltaTime, humanoids);
        break;
      case LanderState.SEEKING_HUMANOID:
        this.updateSeekingHumanoid(deltaTime, humanoids);
        break;
      case LanderState.ABDUCTING:
        this.updateAbducting(deltaTime);
        break;
      case LanderState.ASCENDING:
        this.updateAscending(deltaTime);
        break;
      case LanderState.MUTATING:
        this.updateMutating(deltaTime);
        break;
    }

    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;

    // World wraparound
    if (this.position.x < -this.size.x) {
      this.position.x = this.worldWidth;
    } else if (this.position.x > this.worldWidth) {
      this.position.x = -this.size.x;
    }
  }

  private updateDescending(deltaTime: number, humanoids: Humanoid[]): void {
    // Descend until reaching target depth
    if (this.position.y >= this.descendTarget) {
      this.state = LanderState.SEEKING_HUMANOID;
      this.velocity.y = 0;
      this.findTargetHumanoid(humanoids);
    }
  }

  private updateSeekingHumanoid(deltaTime: number, humanoids: Humanoid[]): void {
    if (!this.targetHumanoid || !this.targetHumanoid.active) {
      this.findTargetHumanoid(humanoids);
      return;
    }

    // Move towards target humanoid
    const dx = this.targetHumanoid.position.x - this.position.x;
    const dy = this.targetHumanoid.position.y - this.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 20) {
      // Close enough to start abduction
      this.startAbduction();
    } else {
      // Move towards humanoid
      this.velocity.x = (dx / distance) * this.speed;
      this.velocity.y = (dy / distance) * this.speed * 0.5; // Slower vertical movement
    }
  }

  private updateAbducting(deltaTime: number): void {
    if (!this.abductedHumanoid || !this.abductedHumanoid.active) {
      this.state = LanderState.SEEKING_HUMANOID;
      return;
    }

    // Move humanoid with lander
    this.abductedHumanoid.position.x = this.position.x;
    this.abductedHumanoid.position.y = this.position.y + this.size.y + 5;

    // Ascend slowly
    this.velocity.x = 0;
    this.velocity.y = -30; // Slower ascent while carrying humanoid

    // Check if reached top of screen
    if (this.position.y < 50) {
      this.state = LanderState.MUTATING;
      this.velocity.y = 0;
    }
  }

  private updateAscending(deltaTime: number): void {
    // Fast ascent without humanoid
    this.velocity.x = 0;
    this.velocity.y = -this.speed * 1.5;

    // Remove when off screen
    if (this.position.y < -this.size.y) {
      this.active = false;
    }
  }

  private updateMutating(deltaTime: number): void {
    // Lander transforms into mutant
    // For now, just remove the lander and humanoid
    if (this.abductedHumanoid) {
      this.abductedHumanoid.active = false; // Humanoid is lost
      this.abductedHumanoid = null;
    }
    
    // TODO: Create mutant enemy here
    this.active = false;
  }

  private findTargetHumanoid(humanoids: Humanoid[]): void {
    // Find nearest unabducted humanoid
    let nearest: Humanoid | null = null;
    let nearestDistance = Infinity;

    humanoids.forEach(humanoid => {
      if (!humanoid.active || humanoid.isAbducted()) return;

      const dx = humanoid.position.x - this.position.x;
      const dy = humanoid.position.y - this.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < nearestDistance) {
        nearest = humanoid;
        nearestDistance = distance;
      }
    });

    this.targetHumanoid = nearest;
  }

  private startAbduction(): void {
    if (!this.targetHumanoid) return;

    this.state = LanderState.ABDUCTING;
    this.abductedHumanoid = this.targetHumanoid;
    this.targetHumanoid.startAbduction(this.id);
    this.targetHumanoid = null;
  }

  public releaseHumanoid(): void {
    // Called when lander is shot while abducting
    if (this.abductedHumanoid) {
      this.abductedHumanoid.fall(); // Humanoid falls
      this.abductedHumanoid = null;
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    // Calculate screen position
    const screenX = this.position.x - this.cameraX;
    const screenY = this.position.y;
    
    // Don't render if off screen
    if (screenX < -20 || screenX > 820) return;

    const centerX = screenX + this.size.x / 2;
    const centerY = screenY + this.size.y / 2;

    this.drawLanderSprite(centerX, centerY);
  }

  private drawLanderSprite(centerX: number, centerY: number): void {
    // Lander sprite with animation
    let pattern: number[][];
    
    if (this.animationFrame === 0) {
      pattern = [
        [0,0,1,1,1,1,0,0],
        [0,1,1,0,0,1,1,0],
        [1,1,0,1,1,0,1,1],
        [1,0,1,1,1,1,0,1],
        [0,1,1,0,0,1,1,0]
      ];
    } else {
      pattern = [
        [0,0,1,1,1,1,0,0],
        [0,1,0,1,1,0,1,0],
        [1,0,1,1,1,1,0,1],
        [1,1,0,1,1,0,1,1],
        [0,1,1,0,0,1,1,0]
      ];
    }

    // Color based on state
    let color = '#883932'; // Red
    if (this.state === LanderState.ABDUCTING) {
      color = '#FFA500'; // Orange when abducting
    } else if (this.state === LanderState.MUTATING) {
      color = '#FFFFFF'; // White when mutating
    }

    // Draw the lander
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = centerX - 8 + col * 2;
          const y = centerY - 5 + row * 2;
          this.renderManager.fillRect(x, y, 2, 2, color);
        }
      }
    }

    // Draw tractor beam if abducting
    if (this.state === LanderState.ABDUCTING && this.abductedHumanoid) {
      this.drawTractorBeam(centerX, centerY + this.size.y / 2);
    }
  }

  private drawTractorBeam(centerX: number, startY: number): void {
    // Simple tractor beam effect
    const beamHeight = 20;
    const beamWidth = 4;
    
    for (let i = 0; i < beamHeight; i += 2) {
      const alpha = 1 - (i / beamHeight);
      const color = `rgba(255, 165, 0, ${alpha * 0.5})`;
      this.renderManager.fillRect(
        centerX - beamWidth / 2,
        startY + i,
        beamWidth,
        2,
        color
      );
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.releaseHumanoid(); // Make sure to release any abducted humanoid
    this.active = false;
  }

  public getPointValue(): number {
    return 150; // Points for destroying a lander
  }

  public getState(): LanderState {
    return this.state;
  }

  public isAbducting(): boolean {
    return this.state === LanderState.ABDUCTING && this.abductedHumanoid !== null;
  }

  public setCameraX(cameraX: number): void {
    this.cameraX = cameraX;
  }

  public setHumanoids(humanoids: Humanoid[]): void {
    this.cachedHumanoids = humanoids;
  }
}